import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  AuthState,
  AuthStep,
  AuthMethodEnum,
  SigninResponse,
  PkReturnError,
} from '../types.d'
import {
  startSignin,
  chooseMethod,
  signupSubmit,
  signinSubmit,
  enrollPasskey,
  signinWithPasskey,
  signinWithAnonymousPasskey,
  completeAuthFlow,
} from './thunks'
import { trackEvent } from './middleware/posthog'

const initialSigninState: AuthState['signin'] = {
  authMethod: null,
  userInfo: null,
  termsAgreed: false,
}

const initialSignupState: AuthState['signup'] = {
  email: '',
  firstName: '',
  lastName: '',
  recaptcha: null,
  passkey: null,
}

const transientStateDefaults = {
  error: null,
  loading: false,
}

export const initialState: AuthState = {
  step: AuthStep.START,
  signin: null,
  signup: null,
  config: {
    returnUrl: '/',
    recaptchaSiteKey: null,
    logoUrl: null,
    clubId: null,
    customDomain: null,
    csrfToken: null,
    passkeyPrompt: null,
    loginHint: null,
    forcePkEnroll: null,
    captchaBypass: false,
    passkeyStorageId: null,
  },
  isDirty: false,
  ...transientStateDefaults,
}

const nextStepMap: Partial<Record<AuthStep, AuthStep>> = {
  [AuthStep.SIGNUP_EMAIL_PROMPT]: AuthStep.SIGNUP_NAME_PROMPT,
  [AuthStep.SIGNUP_NAME_PROMPT]: AuthStep.SIGNUP_CONFIRM,
  [AuthStep.SIGNIN_METHOD_PASSKEY_RETRY]: AuthStep.SIGNIN_METHOD_PASSKEY,
}

const previousStepMap: Partial<Record<AuthStep, AuthStep>> = {
  [AuthStep.SIGNUP_EMAIL_PROMPT]: AuthStep.START,
  [AuthStep.SIGNUP_NAME_PROMPT]: AuthStep.SIGNUP_EMAIL_PROMPT,
  [AuthStep.SIGNUP_CONFIRM]: AuthStep.SIGNUP_NAME_PROMPT,

  [AuthStep.SIGNIN_METHOD_SELECTOR]: AuthStep.START,
  [AuthStep.SIGNIN_METHOD_PASSWORD]: AuthStep.SIGNIN_METHOD_SELECTOR,
  [AuthStep.SIGNIN_METHOD_UNAVAILABLE]: AuthStep.START,
  [AuthStep.SIGNIN_METHOD_PASSKEY_RETRY]: AuthStep.SIGNIN_METHOD_SELECTOR,
  [AuthStep.SIGNIN_METHOD_EMAILOTP]: AuthStep.SIGNIN_METHOD_SELECTOR,
}

const authMethodToStepMap: Record<AuthMethodEnum, AuthStep> = {
  [AuthMethodEnum.EMAIL_OTP]: AuthStep.SIGNIN_METHOD_EMAILOTP,
  [AuthMethodEnum.PASSKEY]: AuthStep.SIGNIN_METHOD_PASSKEY,
  [AuthMethodEnum.PASSWORD]: AuthStep.SIGNIN_METHOD_PASSWORD,
  [AuthMethodEnum.UNAVAILABLE]: AuthStep.SIGNIN_METHOD_UNAVAILABLE,
}

const authMethodToStep = (
  method: AuthMethodEnum | null,
  autoSelected: boolean = false,
): AuthStep => {
  if (method == null) {
    return AuthStep.SIGNIN_METHOD_SELECTOR
  }

  // If passkey was auto-selected, route to retry screen (manual button)
  // If explicitly selected by user, route to auto-prompt screen
  if (method === AuthMethodEnum.PASSKEY && autoSelected) {
    return AuthStep.SIGNIN_METHOD_PASSKEY_RETRY
  }

  return authMethodToStepMap[method]
}

const mapperMaker =
  <K extends string, V extends string>(map: Partial<Record<K, V>>) =>
  (key: K): V => {
    const result = map[key]
    if (result !== undefined) {
      return result
    }
    throw new Error(`No next step defined for ${key}`)
  }

const nextStep = mapperMaker(nextStepMap)
const previousStep = mapperMaker(previousStepMap)

// Helper function to handle post-authentication flow logic
const handlePostAuthFlow = (state: AuthState, payload: SigninResponse) => {
  Object.assign(state, transientStateDefaults)
  state.config.csrfToken = payload.newCsrf

  if (payload.suggestPasskey?.suggest) {
    trackEvent('passkey_suggest', state)
    state.step = AuthStep.SIGNIN_CREATE_PASSKEY_PROMPT
    state.config.passkeyPrompt = payload.suggestPasskey?.prompt
    state.config.passkeyStorageId = payload.suggestPasskey?.storageId
  } else {
    // No passkey suggestion, just set loading state
    // The thunk will handle fetching the URL and redirecting
    state.loading = true
  }
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    initializeState: (state, action: PayloadAction<Partial<AuthState>>) => {
      Object.assign(state, action.payload)
    },
    nextStep: (state) => {
      Object.assign(state, transientStateDefaults)
      state.step = nextStep(state.step)
    },
    previousStep: (state) => {
      const prevStep = previousStep(state.step)

      if (prevStep === AuthStep.START) {
        // Reset to START but preserve config - isDirty will be false from initialState
        Object.assign(state, {
          ...initialState,
          config: state.config, // Preserve existing config
        })
      } else {
        Object.assign(state, transientStateDefaults)
        state.step = prevStep
      }
    },
    setError: (state, action: PayloadAction<string>) => {
      Object.assign(state, transientStateDefaults)
      state.error = action.payload
    },
    setReturnUrl: (state, action: PayloadAction<string>) => {
      state.config.returnUrl = action.payload
    },
    setCsrfToken: (state, action: PayloadAction<string>) => {
      state.config.csrfToken = action.payload
    },
    updateSignupField: (
      state,
      action: PayloadAction<{
        field: keyof NonNullable<AuthState['signup']>
        value: string
      }>,
    ) => {
      if (state.signup) {
        state.signup[action.payload.field] = action.payload.value as any
      }
      state.signin = null
      state.isDirty = true // User has entered signup data
    },
    startSignup: (state, action: PayloadAction<string>) => {
      Object.assign(state, transientStateDefaults)
      state.step = AuthStep.SIGNUP_NAME_PROMPT
      state.signup = { ...initialSignupState, email: action.payload }
      state.signin = null
      state.isDirty = true // User has started signup flow
    },
    passkeyCancel: (state, action: PayloadAction<string>) => {
      Object.assign(state, transientStateDefaults)
      state.error = action.payload
      state.step = AuthStep.SIGNIN_METHOD_PASSKEY_RETRY
    },
    suggestPasskey: (state) => {
      Object.assign(state, transientStateDefaults)
      state.step = AuthStep.SIGNIN_CREATE_PASSKEY_PROMPT
    },
    enrollPasskeyStart: (state) => {
      Object.assign(state, transientStateDefaults)
      state.step = AuthStep.SIGNIN_CREATE_PASSKEY_LOADING
    },
    editSignupEmail: (state) => {
      Object.assign(state, transientStateDefaults)
      state.step = AuthStep.SIGNUP_EMAIL_PROMPT
    },
    editSignupName: (state) => {
      Object.assign(state, transientStateDefaults)
      state.step = AuthStep.SIGNUP_NAME_PROMPT
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    toggleSigninTermsAgreement: (state, action: PayloadAction<boolean>) => {
      if (state.signin) {
        state.signin.termsAgreed = action.payload
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startSignin.pending, (state) => {
        state.loading = true
        state.config.captchaBypass = false // Clear bypass flag after use
      })
      .addCase(startSignin.fulfilled, (state, action) => {
        Object.assign(state, transientStateDefaults)
        state.step = authMethodToStep(
          action.payload.chosenMethod?.type,
          action.payload.autoSelected,
        )
        state.signin = {
          ...initialSigninState,
          authMethod: action.payload.chosenMethod,
          userInfo: action.payload.userInfo,
        }
        state.signup = null
        state.isDirty = true // User has moved beyond START
      })
      .addCase(startSignin.rejected, (state, action) => {
        Object.assign(state, transientStateDefaults)
        state.error = action.error.message || 'An error occurred'
      })

      .addCase(chooseMethod.pending, (state) => {
        state.loading = true
      })
      .addCase(chooseMethod.fulfilled, (state, action) => {
        Object.assign(state, transientStateDefaults)
        state.step = authMethodToStep(
          action.payload?.chosenMethod?.type || null,
          action.payload?.autoSelected || false,
        )
        if (state.signin) {
          state.signin.authMethod = action.payload.chosenMethod
        }
        state.signup = null
        state.isDirty = true // User has chosen a method
      })
      .addCase(chooseMethod.rejected, (state, action) => {
        Object.assign(state, transientStateDefaults)
        state.error = action.error.message || 'An error occurred'
      })

      .addCase(signupSubmit.pending, (state) => {
        state.loading = true
      })
      .addCase(signupSubmit.fulfilled, (state, action) => {
        handlePostAuthFlow(state, action.payload)
      })
      .addCase(signupSubmit.rejected, (state, action) => {
        Object.assign(state, transientStateDefaults)
        state.error = action.error.message || 'An error occurred'
      })

      .addCase(signinSubmit.pending, (state) => {
        state.loading = true
      })
      .addCase(signinSubmit.fulfilled, (state, action) => {
        handlePostAuthFlow(state, action.payload)
      })
      .addCase(signinSubmit.rejected, (state, action) => {
        Object.assign(state, transientStateDefaults)
        state.error =
          (action.payload as string) ||
          action.error.message ||
          'An error occurred'
      })

      .addCase(enrollPasskey.pending, (state) => {
        state.loading = true
      })
      .addCase(enrollPasskey.fulfilled, (state) => {
        Object.assign(state, transientStateDefaults)
        state.step = AuthStep.SIGNIN_CREATE_PASSKEY_SUCCESS
      })
      .addCase(enrollPasskey.rejected, (state, action: any) => {
        Object.assign(state, transientStateDefaults)
        const error = action.payload || {
          message: action.error.message,
          terminal: false,
        }
        state.error = error.message
        state.step = error.terminal
          ? AuthStep.SIGNIN_CREATE_PASSKEY_ERROR_TERMINAL
          : AuthStep.SIGNIN_CREATE_PASSKEY_ERROR
      })

      .addCase(signinWithPasskey.pending, (state) => {
        state.loading = true
      })
      .addCase(signinWithPasskey.fulfilled, (state, action) => {
        handlePostAuthFlow(state, action.payload)
      })
      .addCase(signinWithPasskey.rejected, (state, action) => {
        Object.assign(state, transientStateDefaults)
        if (action.payload !== PkReturnError.SILENT_FAILURE) {
          state.error =
            (action.payload as string) ||
            action.error.message ||
            'An error occurred'
        }
        state.step = AuthStep.SIGNIN_METHOD_PASSKEY_RETRY
      })

      .addCase(signinWithAnonymousPasskey.fulfilled, (state, action) => {
        if (action.payload) {
          Object.assign(state, transientStateDefaults)
          handlePostAuthFlow(state, action.payload)
        } else {
          // Clear loading state even when payload is null (e.g., terminal errors, cancellations)
          state.loading = false
        }
      })
      .addCase(signinWithAnonymousPasskey.rejected, (state, action) => {
        Object.assign(state, transientStateDefaults)
        const payload = action.payload as any
        state.error =
          (typeof payload === 'string' ? payload : payload?.message) ||
          action.error.message ||
          'An error occurred'
      })

      .addCase(completeAuthFlow.pending, (state) => {
        Object.assign(state, transientStateDefaults)
        state.isDirty = false // Clear dirty flag on successful navigation
        state.loading = true
      })
  },
})

export const {
  initializeState,
  nextStep: nextStepAction,
  previousStep: previousStepAction,
  setError,
  setReturnUrl,
  setCsrfToken,
  updateSignupField,
  startSignup,
  passkeyCancel,
  suggestPasskey,
  enrollPasskeyStart,
  editSignupEmail,
  editSignupName,
  setLoading,
  toggleSigninTermsAgreement,
} = authSlice.actions

export const authReducer = authSlice.reducer
export default authSlice.reducer
